import React from 'react';
import {
  DropDownListProps,
  ListItemProps,
  DropDownListOpenEvent,
  DropDownListChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { DropDownList as StyledDropDownList } from '../../simple-styled-components';
import { enums, get } from '../../../../utils';
import dayjs from 'dayjs';
import { Spinner } from '../../../base/spinner';
import { FormContext } from '../../../../contexts/form-context';
import { CallCenterDomainModel } from '../../../../utils/remote-config-manager';
import * as dm from '../../../../data-models';
import { useAvailableCallbackTimes } from '../../../../hooks/use-data';

export interface CustomListItemProps extends ListItemProps {
  dataItem: dm.CallCenterHoursDropDownItem;
}

export interface CustomDropDownListProps
  extends Omit<DropDownListProps, 'data'> {
  data?: any[] | string;
}

const DropDownList: (p: CustomDropDownListProps) => React.ReactElement = (
  p,
) => {
  const [staticMode, setStaticMode] = React.useState(true);
  const [valueReset, setValueReset] = React.useState(false);

  const { formContext } = React.useContext(FormContext);
  const { data: availableTimesData, isFetching } = useAvailableCallbackTimes(
    get(formContext, 'person.companyCode', '0'),
    p.data && typeof p.data === 'string'
      ? dayjs(p.data?.split('.')[1]).format('YYYYMMDD')
      : '',
  );

  React.useEffect(() => {
    if (p.data && typeof p.data === 'string') {
      setStaticMode(false);
    }
  }, [p.data]);

  const onChangeHandler = React.useCallback(
    (e: DropDownListChangeEvent) => {
      if (e.value.disabled) {
        return;
      }
      p.onChange?.(e);
    },
    [p],
  );

  const getData = React.useCallback(() => {
    // currently only supporting available call center times based on type
    if (
      !(p.data as string).startsWith(
        enums.DataSources.availableCallCenterTimesBasedOn,
      )
    ) {
      throw new Error('Invalid data source');
    }

    const closureDates: CallCenterDomainModel[] = get(
      formContext,
      'callCenter.closures',
      [],
    );

    let callbacks = availableTimesData?.availableTimesArray || [];

    callbacks.forEach((hour) => {
      if (
        closureDates.some((d) => {
          return (
            dayjs(d.start).tz('America/New_York') <= dayjs(hour.startTime) &&
            dayjs(d.end).tz('America/New_York') >=
              dayjs(hour.startTime).add(1, 'h')
          );
        }) ||
        new Date() > new Date(hour.startTime)
      ) {
        hour.disabled = true;
      }
    });

    const numberOfAvailableSlots = callbacks.filter(
      (hours) => !hours.disabled,
    ).length;
    if (numberOfAvailableSlots === 0) {
      callbacks = [];
      callbacks.push({
        disabled: true,
        displayTime: enums.ValidationErrors.slotsNotAvailable,
        startTime: '',
      });
    }
    return callbacks;
  }, [availableTimesData?.availableTimesArray, formContext, p.data]);

  React.useEffect(() => {
    if (
      staticMode === false &&
      p.value !== null &&
      p.value !== undefined &&
      !valueReset
    ) {
      const typedPValue = p.value as dm.CallCenterHoursDropDownItem;
      const data = getData();
      // Match on the slot label only. `startTime` embeds the selected day, which
      // changes when the user picks a different contact day, so matching on it
      // too would fail to recognize the same slot on the newly selected day.
      const foundItem = data.find(
        (i) => i.displayTime === typedPValue.displayTime,
      );

      if (
        // if the item is disabled in the list and the selected value is not
        (foundItem?.disabled === true && typedPValue.disabled === false) ||
        // or if the available times list now comes back as empty
        (data.length === 1 &&
          data[0].displayTime === 'No slot available for day')
      ) {
        setValueReset(true);
        setTimeout(() => {
          p.onChange &&
            p.onChange({
              value: undefined,
              reset: true, // custom property to indicate reset
            } as any);
        }, 100);
      } else if (
        foundItem &&
        !foundItem.disabled &&
        foundItem.startTime !== typedPValue.startTime
      ) {
        // The day changed but the same slot is still available: re-sync the
        // selected value to the new day's item so its startTime (and any
        // date derived from it downstream) reflects the currently selected day.
        p.onChange?.({ value: foundItem } as any);
      }
    }
  }, [p.value, staticMode, getData, p, valueReset]);

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: CustomListItemProps,
  ) => {
    if (itemProps?.dataItem?.disabled) {
      const props = {
        ...li.props,
        className: 'k-item k-disabled',
      };
      return React.cloneElement(li, props, li.props.children);
    }
    return li;
  };

  if (p.data && typeof p.data === 'string' && !staticMode && isFetching) {
    return <Spinner></Spinner>;
  }

  return (
    <>
      {p.data && (
        <StyledDropDownList
          {...p}
          data={
            p.data && typeof p.data !== 'string' && staticMode
              ? p.data
              : getData()
          }
          itemRender={itemRender}
          onOpen={(e: DropDownListOpenEvent) => {
            // When wrapped in conversational UI, dropdown intermittently does not recieve focus onOpen.
            // This causes onBlur to not fire, leaving the popup open when clicking outside the parent elem.
            // Fixed in lastest 5.5.0 dev build, but this is a workaround until released in a stable build.
            e.target.focus();
          }}
          onChange={onChangeHandler}
        />
      )}
    </>
  );
};

export default DropDownList;
